<script setup lang="ts">
import { RouterView } from 'vue-router'
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { getTheme, getShowHeader, loadServerConfig, getPortalName } from '@/api/config'
import { initParentScrollTracking } from '@/utils/iframe'

const route = useRoute()
const showHeader = ref(true)
const portalName = ref('Advertiser Portal')
const appContainer = ref<HTMLElement | null>(null)

// ResizeObserver for iframe height reporting
let resizeObserver: ResizeObserver | null = null
let heightInterval: number | null = null
let heightDebounceTimer: number | null = null
let lastReportedHeight = 0
let heightReportingStarted = false

function sendHeightToParent() {
  if (window.parent === window) return

  // Don't send height updates while in fullscreen mode (modal open).
  // Fullscreen sets the iframe to 100vh; a height message would override that.
  if (document.documentElement.classList.contains('iframe-fullscreen')) return

  // Measure the content container element rather than document.documentElement.scrollHeight.
  // scrollHeight of documentElement can never be less than the iframe viewport height,
  // which prevents the iframe from collapsing when content shrinks.
  const el = appContainer.value
  const height = el
    ? el.scrollHeight
    : Math.max(
        document.documentElement.scrollHeight,
        document.body ? document.body.scrollHeight : 0
      )

  if (height > 0 && height !== lastReportedHeight) {
    lastReportedHeight = height
    window.parent.postMessage(
      { type: 'FB_IFRAME_HEIGHT', height: height },
      '*'
    )
  }
}

function sendHeightToParentDebounced() {
  if (heightDebounceTimer) {
    clearTimeout(heightDebounceTimer)
  }
  heightDebounceTimer = window.setTimeout(() => {
    sendHeightToParent()
    heightDebounceTimer = null
  }, 100)
}

function startHeightReporting() {
  if (heightReportingStarted) return
  heightReportingStarted = true

  sendHeightToParent()

  const target = appContainer.value
  if ('ResizeObserver' in window && target) {
    resizeObserver = new ResizeObserver(sendHeightToParentDebounced)
    resizeObserver.observe(target)
  } else {
    heightInterval = window.setInterval(sendHeightToParent, 500)
  }

  window.addEventListener('load', sendHeightToParent)
  window.addEventListener('resize', sendHeightToParentDebounced)

  // Start listening for parent scroll position updates (for modal positioning)
  initParentScrollTracking()
}

function stopHeightReporting() {
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
  if (heightInterval) {
    clearInterval(heightInterval)
    heightInterval = null
  }
  if (heightDebounceTimer) {
    clearTimeout(heightDebounceTimer)
    heightDebounceTimer = null
  }
  window.removeEventListener('load', sendHeightToParent)
  window.removeEventListener('resize', sendHeightToParentDebounced)
}

function applyConfig() {
  const theme = getTheme()
  document.documentElement.setAttribute('data-theme', theme)
  showHeader.value = getShowHeader()
  portalName.value = getPortalName()

  // Always enable embed-mode when in an iframe, regardless of showHeader setting.
  // This ensures modal positioning CSS overrides and height reporting work correctly.
  const inIframe = window.parent !== window

  if (inIframe || !showHeader.value) {
    document.documentElement.classList.add('embed-mode')
    document.body.classList.add('embed-mode')
    startHeightReporting()
  } else {
    document.documentElement.classList.remove('embed-mode')
    document.body.classList.remove('embed-mode')
    stopHeightReporting()
  }

  console.log(`Applied config: theme=${theme}, showHeader=${showHeader.value}, inIframe=${inIframe}`)
}

onMounted(async () => {
  await loadServerConfig()
  applyConfig()
})

onUnmounted(() => {
  stopHeightReporting()
})

watch(() => route.query.t, async () => {
  setTimeout(applyConfig, 100)
})
</script>

<template>
  <div ref="appContainer" class="app-container" :class="{ 'no-header': !showHeader }">
    <!-- Navigation Header -->
    <header v-if="showHeader" class="app-header">
      <div class="header-content">
        <div class="logo">
          <span class="logo-icon">$</span>
          <span class="logo-text">{{ portalName }}</span>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="app-main">
      <RouterView />
    </main>

    <!-- Footer -->
    <footer v-if="showHeader" class="app-footer">
      <p>&copy; {{ new Date().getFullYear() }} AccountScout - Advertiser Portal</p>
    </footer>
  </div>
</template>

<style scoped>
.app-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--bg-primary);
}

.app-header {
  position: sticky;
  top: 0;
  z-index: 100;
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
  backdrop-filter: blur(12px);
}

.header-content {
  max-width: var(--content-max-width);
  margin: 0 auto;
  padding: 0 var(--spacing-6);
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.logo {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  text-decoration: none;
  color: var(--text-primary);
  font-weight: 600;
  font-size: 1.25rem;
}

.logo-icon {
  font-size: 1.5rem;
  color: var(--accent-primary);
  font-weight: 700;
}

.logo-text {
  background: linear-gradient(135deg, var(--accent-primary), var(--accent-secondary));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.app-main {
  flex: 1;
  max-width: var(--content-max-width);
  width: 100%;
  margin: 0 auto;
  padding: var(--spacing-8) var(--spacing-6);
}

.app-footer {
  border-top: 1px solid var(--border-color);
  padding: var(--spacing-6);
  text-align: center;
  color: var(--text-tertiary);
  font-size: 0.875rem;
}

.app-container.no-header .app-main {
  padding-top: var(--spacing-4);
}

.app-container.no-header {
  min-height: 100%;
  height: auto;
}

@media (max-width: 768px) {
  .header-content {
    padding: 0 var(--spacing-4);
  }

  .app-main {
    padding: var(--spacing-6) var(--spacing-4);
  }
}
</style>
